import React, { useEffect, useRef, useState } from 'react';
import '../../styles/MoveFileModal.css';
import CustomButton from '../CustomElements/CustomButton';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import CustomTitle from '../CustomElements/CustomTitle';
import CustomContainer from '../CustomElements/CustomContainer';
import { useDispatch, useSelector } from 'react-redux';
import { load, remove } from '../../redux/actions/loader.action';
import { moveFileToFolderFromServer } from '../../redux/actions/files.action';
import FileItem from './FileItem';
import CustomCheckbox from '../CustomElements/CustomCheckbox';

/**
 * Компонент модального окна для перемещения файла в другую папку.
 *
 * @component
 * @param {Object} props - Свойства компонента.
 * @param {Array} props.data - Данные о файлах и папках.
 * @param {Function} props.onClose - Функция для закрытия модального окна.
 * @param {Object} props.fileToMove - Данные о файле, который нужно переместить.
 * @returns {JSX.Element} - Модальное окно перемещения файла.
 */

const MoveFileModal = ({ data, onClose, fileToMove, t }) => {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();
  // Получаем данные о процессе загрузки
  const loader = useSelector((state) => state.loader);

  // состояние для чекбокс 'all' | 'folders' | 'files'
  const [viewMode, setViewMode] = useState('all');

  // Состояние для хранения списка развернутых папок
  const [expandedFolders, setExpandedFolders] = useState({});
  // Состояние для хранения выбранной папки (id и name)
  const [selectedFolder, setSelectedFolder] = useState(null);
  // Состояние для отображения страницы подтверждения после успешного перемещения файла
  const [addSuccess, setAddSuccess] = useState(false);
  // Таймер для предотвращения одновременного выполнения кликов
  const clickTimeout = useRef(null);

  /**
   * Функция для перемещения файла в выбранную папку.
   * @param {Event} event - Событие клика.
   * @param {string} fileId - ID перемещаемого файла.
   * @param {Object} folder - Объект выбранной папки.
   */
  const moveFileToFolder = (event, fileId, folder) => {
    event.preventDefault();
    dispatch(load()); // Устанавливаем состояние загрузки
    const payload = { parentId: folder?.id };
    dispatch(moveFileToFolderFromServer(payload, fileId));
  };

  /**
   * Функция для разворачивания или сворачивания папки при одиночном клике.
   * @param {Event} event - Событие клика.
   * @param {string} folderId - ID папки.
   */
  const toggleFolder = (event, folderId) => {
    event.stopPropagation();

    // Если уже есть таймер, игнорируем клик
    if (clickTimeout.current) return;

    // Устанавливаем таймер для предотвращения быстрого двойного клика
    clickTimeout.current = setTimeout(() => {
      setExpandedFolders((prev) => ({
        ...prev,
        [folderId]: !prev[folderId], // Инвертируем состояние папки (открыта/закрыта)
      }));
      clickTimeout.current = null;
    }, 250);
  };

  /**
   * Функция для выбора папки при двойном клике.
   * @param {Event} event - Событие клика.
   * @param {Object} folder - Объект папки.
   */
  const handleDoubleClick = (event, folder) => {
    event.stopPropagation();

    // Если таймер активен, сбрасываем его
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current);
      clickTimeout.current = null;
    }

    setSelectedFolder({ id: folder?.id, name: folder?.name }); // Устанавливаем выбранную папку

    // Не меняем состояние разворачивания папки при двойном клике
    setExpandedFolders((prev) => ({
      ...prev,
      [folder?.id]: prev[folder?.id], // Оставляем текущее состояние папки
    }));
  };

  /**
   * Функция для рекурсивного рендеринга списка папок и файлов.
   * Если элемент - папка, он передаётся в компонент `FileItem` и может быть развёрнут.
   * Если папка развёрнута, её содержимое рекурсивно рендерится внутри вложенного `<div>`.
   *
   * @param {Array} items - Список файлов и папок для рендеринга.
   * @param {number} level - Уровень вложенности, используется для отступов.
   * @returns {JSX.Element[]} - Список элементов для рендеринга.
   */
  const renderFolders = (items, level = 0) =>
    items.map((item) => (
      <React.Fragment key={item.id}>
        {/* Рендер отдельного элемента (файл или папка) */}
        <FileItem
          item={item}
          expandedFolders={expandedFolders}
          selectedFolder={selectedFolder}
          toggleFolder={toggleFolder}
          handleDoubleClick={handleDoubleClick}
          level={level}
          viewMode={viewMode}
        />

        {/* Если папка развёрнута и у неё есть дети, рекурсивно рендерим их */}
        {expandedFolders[item.id] && item.children && (
          <div className="file-manager-nested-folders">{renderFolders(item.children, level + 1)}</div>
        )}
      </React.Fragment>
    ));

  // Очистка таймера при размонтировании компонента
  useEffect(() => {
    return () => {
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
      }
    };
  }, []);

  // Отслеживание состояния загрузки
  useEffect(() => {
    // Если загрузка завершена и нет ошибок, показываем подтверждение перемещения
    if (!loader?.isLoading && loader?.isSuccess) {
      setAddSuccess(true);
    }
    // При закрытии компонента очищаем стейт загрузки
    return () => {
      dispatch(remove());
    };
  }, [loader?.isLoading, loader?.isSuccess, dispatch]);

  return (
    <CustomContainer>
      <CustomTitle titleText={t?.moveFileTitle || 'ПЕРЕМЕЩЕНИЕ ФАЙЛА'} />
      <p>{addSuccess ? '' : t?.moveFileInstruction || 'Выберите папку для перемещения (двойной клик)'}</p>

      {addSuccess ? (
        <ConfirmationPage
          confirmText={t?.moveFileSuccess || 'Файл успешно перемещен!'}
          onConfirm={onClose}
          addNew={t?.moveFileBackToList || 'К списку'}
        />
      ) : (
        <>
          <div className="view-mode-checkboxes">
            <CustomCheckbox
              width="var(--sadr-font-size-186)"
              height="var(--sadr-font-size-25)"
              label={t?.showAll || 'Показать всё'}
              checked={viewMode === 'all'}
              onChange={() => setViewMode('all')}
            />
            <CustomCheckbox
              width="var(--sadr-font-size-260)"
              height="var(--sadr-font-size-25)"
              label={t?.showOnlyFolders || 'Показать только папки'}
              checked={viewMode === 'folders'}
              onChange={() => setViewMode('folders')}
            />
            <CustomCheckbox
              width="var(--sadr-font-size-320)"
              height="var(--sadr-font-size-25)"
              label={t?.showOnlyFiles || 'Показать только файлы'}
              checked={viewMode === 'files'}
              onChange={() => setViewMode('files')}
            />
          </div>
          <div className="file-manager-folder-list">{renderFolders(data)}</div>

          <div className={`file-manager-confirm-move ${!selectedFolder ? 'hidden' : ''}`}>
            <p>
              {t?.moveFileConfirmQuestion || 'Переместить файл'} <strong>{fileToMove.name}</strong>{' '}
              {t?.moveFileToFolder || 'в папку'}{' '} <strong>{selectedFolder?.name}</strong>?
            </p>
          </div>

          <div className="form-container_submit-button height-unset padding-20">
            <CustomButton
              width="var(--sadr-font-size-420)"
              height="var(--sadr-font-size-60)"
              margin={'0 0 var(--sadr-font-size-20) 0'}
              text={t?.confirmButton || 'Подтвердить'}
              onClick={(event) => moveFileToFolder(event, fileToMove.id, selectedFolder)}
              disabled={!selectedFolder}
              isButton
            />
            <CustomButton
              width="var(--sadr-font-size-420)"
              height="var(--sadr-font-size-60)"
              margin={'0 0 var(--sadr-font-size-20) 0'}
              text={t?.cancelButton || 'Отмена'}
              onClick={onClose}
              disabled={false}
              isButton
              colorText="#1C221D"
              color="var(--sadr-background-secondary)"
              border
            />
          </div>
        </>
      )}
    </CustomContainer>
  );
};

export default MoveFileModal;
